import React, { useState, useEffect } from 'react';
import { Button, Modal, message } from 'antd';
import {
  ExpandOutlined,
  CompressOutlined,
  FormatPainterOutlined,
  CloseOutlined,
  CheckOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { Editor } from '@monaco-editor/react';
import styled from 'styled-components';
import { formatSQL, flattenSQL, configureSqlLanguage } from '../../../../../utils/sqlFormatter';
import { useValidateFlowPattern } from '../../../../../api/queryHooks';

const SqlHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const SqlLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    padding: 0;
  }

  .ant-modal-body {
    padding: 24px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
`;

interface SqlEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  isView?: boolean;
  placeholder?: string;
}

type ValidationStatus = 0 | 1 | 2; // 0 = error, 1 = success, 2 = idle

const SqlEditor: React.FC<SqlEditorProps> = ({ value = '', onChange, isView = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState(value);
  const [validationStatus, setValidationStatus] = useState<ValidationStatus>(2); // 2 = idle

  const validatePatternMutation = useValidateFlowPattern();

  // Sync displayValue when prop value changes from outside
  useEffect(() => {
    if (value !== flattenSQL(displayValue)) {
      setDisplayValue(value);
    }
  }, [value, displayValue]);

  const handleExpandClick = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleEditorChange = (newValue: string | undefined) => {
    const rawValue = newValue || '';
    // Update display value to keep formatting in both editors
    setDisplayValue(rawValue);

    // Reset validation status when content changes
    setValidationStatus(2); // 2 = idle

    // Always flatten newlines for form data - use utility function
    const finalValue = flattenSQL(rawValue);

    onChange?.(finalValue);
  };

  const handleFormatClick = () => {
    if (!displayValue || !displayValue.trim()) return;

    const formattedSQL = formatSQL(displayValue);

    if (formattedSQL) {
      setDisplayValue(formattedSQL);

      // Reset validation status since content has changed
      setValidationStatus(2); // 2 = idle

      const flatValue = flattenSQL(formattedSQL);
      onChange?.(flatValue);
    }
  };

  const handleValidateClick = async () => {
    if (!displayValue || !displayValue.trim()) return;

    setValidationStatus(2); // 2 = idle (loading state will be handled by mutation)
    const result = await validatePatternMutation.mutateAsync({
      pattern: displayValue.trim(),
    });
    setValidationStatus(result?.data?.is_validation_passed || 0);
    if (result?.data?.error_msg) {
      const errorMsg = result.data.error_msg;
      message.error(`SQL validation failed: ${errorMsg}`);
    }
  };

  const handleEditorDidMount = (editor: any, monaco: any) => {
    // Configure SQL language and completion provider using utility
    configureSqlLanguage(monaco);
  };

  const handleModalEditorDidMount = (editor: any, monaco: any) => {
    // Configure SQL language and completion provider using utility (Monaco handles duplicates)
    configureSqlLanguage(monaco);
  };

  const actionButtons = () => {
    const icon =
      validationStatus === 1 ? (
        <CheckOutlined />
      ) : validationStatus === 0 ? (
        <CloseOutlined />
      ) : (
        <RedoOutlined />
      );

    const type = validationStatus === 1 ? 'primary' : undefined;
    const danger = validationStatus === 0;
    const style =
      validationStatus === 1 ? { backgroundColor: '#52c41a', borderColor: '#52c41a' } : undefined;

    return (
      <div style={{ display: 'flex', gap: 8 }}>
        <Button
          icon={icon}
          onClick={handleValidateClick}
          loading={validatePatternMutation.isLoading}
          title="Validate SQL"
          size="small"
          danger={danger}
          type={type}
          style={style}
        >
          Validate
        </Button>
        <Button
          icon={<FormatPainterOutlined />}
          onClick={handleFormatClick}
          title="Format SQL"
          size="small"
        >
          Format
        </Button>
      </div>
    );
  };

  return (
    <div>
      <SqlHeader>
        <SqlLabel>SQL Query</SqlLabel>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!isView && actionButtons()}
          <Button
            icon={<ExpandOutlined />}
            onClick={handleExpandClick}
            title="Expand to fullscreen"
            size="small"
          >
            Expand
          </Button>
        </div>
      </SqlHeader>

      <div
        style={{
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          overflow: 'hidden',
          backgroundColor: 'transparent',
        }}
      >
        <Editor
          height="200px"
          defaultLanguage="sql"
          value={displayValue}
          onChange={handleEditorChange}
          onMount={handleEditorDidMount}
          theme={isView ? 'view-mode' : 'vs'}
          options={{
            readOnly: isView,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 13,
            fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
            lineNumbers: 'on',
            wordWrap: 'on',
            automaticLayout: true,
            tabSize: 2,
            insertSpaces: true,
            contextmenu: false,
            selectOnLineNumbers: true,
            roundedSelection: false,
            scrollbar: {
              vertical: 'auto',
              horizontal: 'auto',
            },
            // Enable SQL suggestions and IntelliSense
            quickSuggestions: true,
            suggestOnTriggerCharacters: true,
            acceptSuggestionOnCommitCharacter: true,
            acceptSuggestionOnEnter: 'on',
            wordBasedSuggestions: 'currentDocument',
            parameterHints: { enabled: true },
            // Enable language-specific suggestions (SQL keywords)
            suggest: {
              showKeywords: true,
              showSnippets: true,
              showFunctions: true,
              showVariables: true,
              showValues: true,
              showWords: true,
              showColors: false,
              showFiles: false,
              showReferences: false,
              showFolders: false,
              showTypeParameters: false,
              showIssues: false,
              showUsers: false,
            },
          }}
        />
      </div>

      <StyledModal
        title={null}
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width="85vw"
        style={{ maxWidth: '1200px' }}
        centered
        destroyOnClose
      >
        <ModalHeader>
          <ModalTitle>SQL Query Editor</ModalTitle>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginRight: 24 }}>
            {!isView && actionButtons()}
            <Button
              icon={<CompressOutlined />}
              onClick={handleModalClose}
              title="Exit fullscreen"
              size="small"
            >
              Exit Fullscreen
            </Button>
          </div>
        </ModalHeader>

        <div
          style={{
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            overflow: 'hidden',
            height: '800px',
            backgroundColor: 'transparent',
          }}
        >
          <Editor
            height="100%"
            defaultLanguage="sql"
            value={displayValue}
            onChange={handleEditorChange}
            onMount={handleModalEditorDidMount}
            theme={isView ? 'view-mode' : 'vs'}
            options={{
              readOnly: isView,
              minimap: { enabled: true },
              scrollBeyondLastLine: false,
              fontSize: 14,
              fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
              lineNumbers: 'on',
              wordWrap: 'on',
              automaticLayout: true,
              tabSize: 2,
              insertSpaces: true,
              contextmenu: true,
              selectOnLineNumbers: true,
              roundedSelection: false,
              scrollbar: {
                vertical: 'auto',
                horizontal: 'auto',
              },
              // Enable SQL suggestions and IntelliSense
              quickSuggestions: true,
              suggestOnTriggerCharacters: true,
              acceptSuggestionOnCommitCharacter: true,
              acceptSuggestionOnEnter: 'on',
              wordBasedSuggestions: 'currentDocument',
              parameterHints: { enabled: true },
              // Enable language-specific suggestions (SQL keywords)
              suggest: {
                showKeywords: true,
                showSnippets: true,
                showFunctions: true,
                showVariables: true,
                showValues: true,
                showWords: true,
                showColors: false,
                showFiles: false,
                showReferences: false,
                showFolders: false,
                showTypeParameters: false,
                showIssues: false,
                showUsers: false,
              },
            }}
          />
        </div>
      </StyledModal>
    </div>
  );
};

export default SqlEditor;
